import { useEffect, useState } from 'react';

/**
 * Resolves to `true` only once the image at `url` has been fetched and
 * fully decoded by the browser, so the consumer can fade it in without
 * a half-painted flash. Uses `HTMLImageElement.decode()` (with onload
 * fallback) — the modern primitive for "is this paintable yet?".
 *
 * Returns `false` immediately when `url` becomes null/changes, so a new
 * offer's bg image starts hidden and crossfades in.
 */
export function useImageReady(url: string | null | undefined): boolean {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(false);
    if (!url) return;

    let cancelled = false;
    const img = new Image();
    img.src = url;

    const markReady = () => {
      if (!cancelled) setReady(true);
    };

    if (typeof img.decode === 'function') {
      img.decode().then(markReady).catch(markReady);
    } else {
      img.onload = markReady;
      img.onerror = markReady;
    }

    return () => {
      cancelled = true;
    };
  }, [url]);

  return ready;
}
